import { useMemo } from 'react';

import dayjs from 'dayjs';

import { IS_NATERA } from '@constants/constants';
import { Clinician } from '@customTypes/clinician';
import { ExtendedOrder, OrderStatus } from '@customTypes/order';
import { Requisition } from '@customTypes/requisition';
import { getClinicianName } from '@helpers/clinician';
import { isOrderReported } from '@helpers/order';
import { getPatientName } from '@helpers/patient';
import { getTimelineStatusLabel } from '@helpers/requisition';

interface UseRequisitionResult {
  clinicians: Array<Clinician & { formattedName: string }>;
  patientName: string;
  requisitionId: string;
  requisitionDate: string;
  orders: ExtendedOrder[];
  requisitionStatus: string | null;
  isCanceled: boolean;
  isCompleted: boolean;
  canDowngrade: boolean;
  disableDowngradeOrder: boolean;
}

const useRequisition = (requisition?: Requisition | null): UseRequisitionResult => {
  const patientName = useMemo(
    () => getPatientName(requisition?.subject?.name ?? requisition?.name),
    [requisition?.name, requisition?.subject],
  );
  const requisitionId = useMemo(() => requisition?.requisition_id ?? '', [requisition?.requisition_id]);
  const submittedAt = requisition?.submitted_at;
  const requisitionDate = useMemo(() => {
    if (!submittedAt) {
      return '';
    }

    return dayjs.utc(submittedAt).format('MM/DD/YYYY');
  }, [submittedAt]);

  const orders = useMemo(
    () =>
      (requisition?.orders || []).map(order => ({
        ...order,
        created_at: dayjs.utc(order.created_at).format('MM/DD/YYYY'),
        isReported: isOrderReported(order),
      })),
    [requisition?.orders],
  );

  const isCanceled = useMemo(
    () => orders.every(order => [OrderStatus.CANCELED, OrderStatus.DENIED, OrderStatus.FAILED].includes(order.status)),
    [orders],
  );

  /**
   * For now, we can downgrade if
   * there is at least one order with the product ids.
   * We will find a better way for that later.
   */
  const canDowngrade = useMemo(
    () =>
      !!requisition?.case_requisitions?.length &&
      requisition?.case_requisitions.some(case_requisition =>
        case_requisition.orders.some(order => order.status !== OrderStatus.CANCELED),
      ) &&
      !IS_NATERA &&
      !isCanceled,
    [requisition, isCanceled],
  );

  const requisitionStatus: string | null = useMemo(() => getTimelineStatusLabel(requisition), [requisition]);

  const isCompleted = useMemo(() => orders.every(isOrderReported), [orders]);

  const disableDowngradeOrder = useMemo(() => {
    // Disable if a main order status is Complete or Canceled
    if (isCanceled || isCompleted) {
      return true;
    }
    // Disable if both main order and related orders status are Analyzing (sampple is accessioned)
    if (requisition?.case_requisitions?.length) {
      return requisition.case_requisitions.every(requisition => !!requisition.sample_received_at);
    }

    return false;
  }, [isCanceled, isCompleted, requisition]);

  const clinicians = useMemo(
    () =>
      (requisition?.clinicians || []).map(clinician => ({
        ...clinician,
        formattedName: getClinicianName(clinician),
      })),
    [requisition],
  );

  return {
    clinicians,
    patientName,
    requisitionId,
    requisitionDate,
    orders,
    requisitionStatus,
    isCanceled,
    isCompleted,
    canDowngrade,
    disableDowngradeOrder,
  };
};

export default useRequisition;
